import { useEffect, useState } from "react";
import { Anchor, ArrowLeftRight, Building2, Coins, ExternalLink, Flame, Ship, Signpost, Swords, X } from "lucide-react";
import { api } from "@/lib/api";
import { BuildingInfo, Face, MansionInfo, MyLadder, onX } from "@/city3d/Hud";
import { pointOf } from "./CoinFly";
import { Btn, PANEL, Pill } from "./kit";
import { ACC, SHIP, fmt, tradeBlock } from "./util";

// An icon tile: the icon in its category's colour and a number; the label is the tooltip.
const Stat = ({ icon: Icon, acc, label, v }) => (
  <div title={label} className="hud-well flex flex-col items-center gap-0.5 rounded-[10px] px-1 py-1.5" style={{ "--acc": ACC[acc] }}>
    <Icon className="acc-text h-4 w-4" strokeWidth={2.2} />
    <b className="text-sm font-semibold tabular-nums">{v}</b>
  </div>
);

const LINK = "chip-btn chip-soft flex-1 px-3 py-2 text-center text-sm";

// A tapped house: who lives there, how rich, and what you can do about it.
export function Inspector({ house, nb, user, econ, mayorK, onTrade, onChallenge, onOpen, onFly, onChanged, onLeft, onClose }) {
  const o = house.owner, lit = o.s === "f", mine = o.k === user?.k, why = tradeBlock(o, econ, user?.k);
  const [d, setD] = useState(null);
  const load = () => api(`/residents/${o.h}`).then(setD).catch(() => {});
  useEffect(() => {
    let live = true;
    api(`/residents/${o.h}`).then((r) => live && setD(r)).catch(() => {});
    return () => { live = false; };
  }, [o.h, o.s, o.e, o.m, house.stack.length]);
  const fresh = d?.k === o.k ? d : null;
  const [statusAcc, status] = !lit ? ["grey", "Dark"] : o.k === mayorK ? ["gold", "Mayor"] : house.mansion ? ["rose", `Mansion #${house.mansion}`] : o.u ? ["green", "Uptown"] : ["blue", "Docks"];
  const floors = house.stack.length > 1 ? `floor ${house.level + 1} of ${house.stack.length}` : `${o.f} ${o.f === 1 ? "floor" : "floors"}`;

  return (
    <div className={`${PANEL} pop hud-scroll pointer-events-auto max-h-[70vh] w-[min(92vw,340px)] overflow-y-auto rounded-[20px] p-3`} data-testid="inspector">
      <div className="flex items-start gap-3">
        <Face p={o} size={48} ring={lit ? ACC.gold : "var(--x-line)"} dim={!lit} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-lg font-semibold">@{o.h}{mine && <span className="acc-fill ml-1.5 rounded-full px-1.5 py-px align-middle text-[11px] font-semibold">You</span>}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Pill acc={statusAcc}>{status}</Pill>
            <Pill acc="grey" title={floors}><Building2 className="h-3 w-3" />{house.stack.length > 1 ? `${house.level + 1}/${house.stack.length}` : o.f}</Pill>
            {nb && <Pill style={{ "--acc": nb.color }}>{nb.name || "New district"}</Pill>}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" data-testid="inspector-close" className="hud-well grid h-7 w-7 place-items-center rounded-full text-x-mute transition-colors hover:bg-x-active hover:text-x-ink"><X className="h-4 w-4" /></button>
      </div>

      {lit ? (
        <>
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            <Stat icon={Coins} acc="gold" label="Worth" v={fmt(o.e)} />
            <Stat icon={Flame} acc="grey" label="This week" v={`+${fmt(o.wk)}`} />
            <Stat icon={Anchor} acc="blue" label="Berths" v={`${o.sh.length}/${o.br}`} />
            <Stat icon={ArrowLeftRight} acc="green" label="Trades today" v={o.vs} />
          </div>
          <div className="mt-2 flex min-h-7 items-center gap-1.5" data-testid="inspector-ships" title="Ships docked">
            <Anchor className="h-4 w-4 text-x-mute" />
            {o.sh.length ? o.sh.map(([id], i) => { const Boat = SHIP[id] ?? Ship; return <Boat key={i} className="h-5 w-5 text-x-accent" strokeWidth={2} />; }) : <span className="text-sm text-x-mute">No ships out</span>}
          </div>
          {o.sg && (
            <a href={o.sg.l} target="_blank" rel="noopener noreferrer nofollow" title={o.sg.l} data-testid="inspector-sign"
              className="hud-well mt-2 flex items-center gap-2 rounded-[10px] px-2.5 py-1.5 text-sm font-semibold transition-colors hover:bg-x-active">
              <Signpost className="h-4 w-4 shrink-0 text-x-gold" /><span className="min-w-0 flex-1 truncate">{o.sg.c}</span><ExternalLink className="h-3.5 w-3.5 shrink-0 text-x-mute" />
            </a>
          )}
        </>
      ) : <div className="mt-2 text-sm text-x-mute" title="Their dock is waiting for them.">Not moved in yet</div>}

      <div>
        {fresh?.mansion && <MansionInfo m={fresh.mansion} mine={fresh.is_me} />}
        {fresh && <BuildingInfo b={fresh.building} k={o.k} onVisit={onFly} share={fresh.is_me && fresh.building.floor === 1 && lit ? fresh.tower_share : null} />}
        {fresh?.is_me && fresh.ladder && <MyLadder l={fresh.ladder} onChanged={async () => { await load(); await onChanged(); }} onLeft={onLeft} />}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {mine ? <Btn className="flex flex-1 items-center justify-center gap-1.5 py-2" onClick={() => onOpen("economy", "dock")}><Anchor className="h-4 w-4" />My dock</Btn> : lit && (
          <>
            <Btn tone="green" className="flex flex-1 items-center justify-center gap-1.5 py-2 text-sm" disabled={Boolean(why)} title={why ?? "Trade"} data-testid="visit-button" onClick={(e) => onTrade(o, pointOf(e.currentTarget))}><ArrowLeftRight className="h-4 w-4" />{why ?? "Trade"}</Btn>
            <Btn tone="rose" className="flex flex-1 items-center justify-center gap-1.5 py-2 text-sm" disabled={!econ} data-testid="challenge-button" onClick={() => onChallenge(o)}><Swords className="h-4 w-4" />Challenge</Btn>
          </>
        )}
        {user && !lit && fresh && <a href={onX(fresh.share.text, fresh.share.url)} target="_blank" rel="noreferrer" className={LINK}>Tag them on X</a>}
        <a href={`https://x.com/${o.h}`} target="_blank" rel="noreferrer" className={LINK} data-testid="profile-link">Profile</a>
      </div>
    </div>
  );
}
